// src/components/Navbar.jsx
import React, { useState, useRef, useEffect } from "react";
import gsapImported from "gsap";

const navItems = [
    { name: "Home", href: "#home" },
    { name: "About", href: "#about" },
    { name: "Works", href: "#Portfolio" },
    { name: "Contact", href: "#contact" },
];

const AnimatedMenuIcon = ({ onClick, line1Ref, line3Ref }) => (
    <button
        onClick={onClick}
        className="relative flex h-7 w-8 items-center justify-center focus:outline-none md:hidden"
    >
        <span
            ref={line1Ref}
            className="absolute block h-0.5 w-7 bg-text-light rounded-full top-2 transition-all"
        />
        <span
            ref={line3Ref}
            className="absolute block h-0.5 w-7 bg-text-light rounded-full top-4 transition-all"
        />
    </button>
);

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);

    // refs
    const logoRef = useRef(null);
    const desktopLinksRef = useRef(null);
    const mobileMenuRef = useRef(null);
    const line1Ref = useRef(null);
    const line3Ref = useRef(null);

    const toggleMenu = () => setIsOpen((p) => !p);

    // ---------- LOAD ANIMATION ----------
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const _gsap = gsapImported ?? window.gsap;
        if (!_gsap) {
            // fallback: ensure elements are visible
            if (logoRef.current) logoRef.current.style.opacity = 1;
            if (desktopLinksRef.current) {
                [...desktopLinksRef.current.children].forEach((c) => {
                    c.style.opacity = 1;
                });
            }
            return;
        }

        _gsap.set(logoRef.current, { opacity: 0, y: -10 });
        _gsap.set(desktopLinksRef.current?.children, { opacity: 0, y: -10 });

        _gsap.to(logoRef.current, {
            opacity: 1,
            y: 0,
            duration: 0.4,
            ease: "power2.out",
        });

        _gsap.to(desktopLinksRef.current.children, {
            opacity: 1,
            y: 0,
            duration: 0.4,
            stagger: 0.1,
            ease: "power2.out",
            delay: 0.1,
        });
    }, []);

    // ---------- MOBILE MENU ANIMATION ----------
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const _gsap = gsapImported ?? window.gsap;
        if (!_gsap) return;

        if (isOpen) {
            _gsap.to(mobileMenuRef.current, {
                height: "auto",
                duration: 0.4,
                ease: "power3.inOut",
            });

            _gsap.fromTo(
                ".mobile-link",
                { opacity: 0, y: -10 },
                { opacity: 1, y: 0, duration: 0.3, stagger: 0.08 }
            );

            _gsap.to(line1Ref.current, { rotate: 45, top: "1.25rem", duration: 0.3 });
            _gsap.to(line3Ref.current, { rotate: -45, top: "1.25rem", duration: 0.3 });
        } else {
            _gsap.to(".mobile-link", {
                opacity: 0,
                y: -5,
                duration: 0.2,
                stagger: -0.05,
            });

            _gsap.to(mobileMenuRef.current, {
                height: 0,
                duration: 0.35,
                ease: "power3.inOut",
                delay: 0.05,
            });

            _gsap.to(line1Ref.current, { rotate: 0, top: "0.5rem", duration: 0.3 });
            _gsap.to(line3Ref.current, { rotate: 0, top: "1.25rem", duration: 0.3 });
        }
    }, [isOpen]);

    return (
        <nav className="fixed top-0 left-0 right-0 bg-background/90 backdrop-blur-md z-50 border-b border-accent/10">
            <div className="mx-auto px-6 md:px-12 lg:px-20 xl:px-32">
                <div className="flex items-center justify-between h-20">
                    {/* Logo */}
                    <div
                        ref={logoRef}
                        className="text-text-light text-xl md:text-2xl font-extrabold tracking-wide"
                    >
                        Bhavya<span className="text-primary text-3xl">.</span>
                    </div>

                    {/* Desktop Links */}
                    <div
                        ref={desktopLinksRef}
                        className="hidden md:flex items-center space-x-8"
                    >
                        {navItems.map((item) => (
                            <button
                                key={item.name}
                                onClick={() => {
                                    document.querySelector(item.href)?.scrollIntoView({
                                        behavior: "smooth",
                                    });
                                }}
                                className="text-text-light hover:text-accent font-medium text-base relative group"
                            >
                                {item.name}
                                <span className="absolute left-0 bottom-0 h-[2px] w-0 bg-accent transition-all duration-300 group-hover:w-full rounded-full"></span>
                            </button>
                        ))}
                    </div>

                    {/* Mobile Menu Button */}
                    <AnimatedMenuIcon
                        onClick={toggleMenu}
                        line1Ref={line1Ref}
                        line3Ref={line3Ref}
                    />
                </div>

                {/* Mobile Menu Panel */}
                <div
                    ref={mobileMenuRef}
                    className="md:hidden overflow-hidden h-0 transition-all"
                >
                    <div className="flex flex-col space-y-2 py-4">
                        {navItems.map((item) => (
                            <button
                                key={item.name}
                                onClick={() => {
                                    // close menu
                                    setIsOpen(false);
                                    // smooth scroll
                                    document.querySelector(item.href)?.scrollIntoView({
                                        behavior: "smooth",
                                    });
                                }}
                                className="mobile-link block text-text-light hover:text-primary transition-colors font-semibold py-3 px-2 rounded-lg text-lg text-center"
                            >
                                {item.name}
                            </button>
                        ))}

                    </div>
                </div>
            </div>
        </nav>
    );
};

export default Navbar;
